import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Users, Crown, MapPin } from 'lucide-react';
import { useCohortDetail } from '@/lib/hooks/use-certification-cohorts';
import { Skeleton } from '@/components/ui/skeleton';

interface CohortDetailProps {
  cohortId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_COLORS: Record<string, string> = {
  forming: 'bg-blue-100 text-blue-700',
  full: 'bg-amber-100 text-amber-700',
  submitted: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
};

export function CohortDetail({ cohortId, open, onOpenChange }: CohortDetailProps) {
  const { cohort, members, isLoading } = useCohortDetail(cohortId);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              <>
                {cohort?.name}
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[cohort?.status || ''] || ''}`}>
                  {cohort?.status}
                </span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : cohort ? (
          <div className="space-y-6">
            {cohort.description && (
              <p className="text-sm text-muted-foreground">{cohort.description}</p>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  Members
                </span>
                <span className="font-semibold">{members.length}/{cohort.max_members}</span>
              </div>
              <Progress value={(members.length / cohort.max_members) * 100} className="h-2" />
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Team Members</h4>
              {members.map((member: any) => {
                const profile = member.freelancer_profiles;
                const initials = profile?.display_name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || '??';
                return (
                  <div key={member.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={profile?.profile_picture_url || undefined} />
                      <AvatarFallback className="text-xs bg-color-1/10 text-color-1">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{profile?.display_name}</span>
                        {member.role === 'organizer' && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 gap-0.5">
                            <Crown className="h-2.5 w-2.5" />
                            Organizer
                          </Badge>
                        )}
                      </div>
                      {profile?.location && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-2.5 w-2.5" />
                          {profile.location}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {members.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No members yet</p>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
